import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/Button";
import permissionsService from "../services/permissionsService";

const ACTIONS = [
  { key: "can_view", label: "Ver" },
  { key: "can_create", label: "Criar" },
  { key: "can_edit", label: "Editar" },
  { key: "can_delete", label: "Excluir" },
];

const isAdminProfile = (p) => (p || "").toLowerCase() === "administrador";
const keyOf = (profile, screenId) => `${profile}|${screenId}`;

const Permissoes = () => {
  const [screens, setScreens] = useState([]);
  const [perms, setPerms] = useState({}); // `${profile}|${screenId}` -> {can_view,...}
  const [profiles, setProfiles] = useState([]); // catálogo: {id,name,is_system,is_admin,user_count}
  const [activeProfile, setActiveProfile] = useState("Analista");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [newProfileName, setNewProfileName] = useState("");
  const [addingProfile, setAddingProfile] = useState(false);
  const [busyProfile, setBusyProfile] = useState(false);

  const loggedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  }, []);

  const activeTenantName = useMemo(
    () => localStorage.getItem("active_tenant_name") || "",
    []
  );

  const isAdmin =
    loggedUser?.profile?.toLowerCase() === "administrador" ||
    loggedUser?.profile?.toLowerCase() === "admin";

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const [screenList, permList, profileList] = await Promise.all([
        permissionsService.getScreens(),
        permissionsService.getPermissions(),
        permissionsService.getProfiles(),
      ]);
      setScreens(screenList || []);
      const profs = Array.isArray(profileList) ? profileList : [];
      setProfiles(profs);
      // Mantém o perfil ativo se ainda existir; senão cai no 1º não-admin.
      setActiveProfile((cur) => {
        const names = profs.map((p) => p.name);
        if (names.includes(cur)) return cur;
        const firstEditable = profs.find((p) => !p.is_admin);
        return (firstEditable || profs[0])?.name || "Analista";
      });

      const map = {};
      (permList || []).forEach((p) => {
        map[keyOf(p.profile, p.screen_id)] = {
          can_view: !!p.can_view,
          can_create: !!p.can_create,
          can_edit: !!p.can_edit,
          can_delete: !!p.can_delete,
        };
      });
      setPerms(map);
    } catch (err) {
      console.error("Erro ao carregar permissões:", err);
      setError(err.response?.data?.detail || "Erro ao carregar permissões.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async () => {
    const name = newProfileName.trim();
    if (!name) return;
    setBusyProfile(true);
    setError("");
    setMessage("");
    try {
      await permissionsService.createProfile(name);
      setNewProfileName("");
      setAddingProfile(false);
      await load();
      setActiveProfile(name);
      setMessage(`Perfil "${name}" criado. Configure as telas que ele pode ver e salve.`);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao criar o perfil.");
    } finally {
      setBusyProfile(false);
    }
  };

  const handleDeleteProfile = async (p) => {
    if (!p?.id || p.is_system) return;
    if (!window.confirm(`Excluir o perfil "${p.name}"? Isso remove a grade de permissões dele.`)) return;
    setBusyProfile(true);
    setError("");
    setMessage("");
    try {
      await permissionsService.deleteProfile(p.id);
      if (activeProfile === p.name) setActiveProfile("Analista");
      await load();
      setMessage(`Perfil "${p.name}" excluído.`);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao excluir o perfil.");
    } finally {
      setBusyProfile(false);
    }
  };

  // Permissoes de uma celula (default tudo falso; Administrador = tudo verdadeiro).
  const cell = (profile, screenId) => {
    if (isAdminProfile(profile)) {
      return { can_view: true, can_create: true, can_edit: true, can_delete: true };
    }
    return (
      perms[keyOf(profile, screenId)] || {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
      }
    );
  };

  const toggle = (profile, screenId, action) => {
    if (isAdminProfile(profile)) return; // acesso total imutavel
    setMessage("");
    setPerms((prev) => {
      const k = keyOf(profile, screenId);
      const current = prev[k] || {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
      };
      const next = { ...current, [action]: !current[action] };
      // Sem visibilidade nao faz sentido manter acoes ligadas.
      if (action === "can_view" && !next.can_view) {
        next.can_create = false;
        next.can_edit = false;
        next.can_delete = false;
      }
      return { ...prev, [k]: next };
    });
  };

  const handleSave = async () => {
    if (isAdminProfile(activeProfile)) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const rows = screens.map((s) => {
        const c = cell(activeProfile, s.id);
        return {
          profile: activeProfile,
          screen_id: s.id,
          can_view: c.can_view,
          can_create: c.can_create,
          can_edit: c.can_edit,
          can_delete: c.can_delete,
        };
      });
      await permissionsService.savePermissionsBulk(rows);
      setMessage(`Permissões do perfil "${activeProfile}" salvas com sucesso.`);
    } catch (err) {
      console.error("Erro ao salvar permissões:", err);
      setError(err.response?.data?.detail || "Erro ao salvar permissões.");
    } finally {
      setSaving(false);
    }
  };

  // Agrupa as telas por module preservando a ordem de chegada (id asc).
  const grouped = useMemo(() => {
    const groups = [];
    const index = {};
    screens.forEach((s) => {
      const mod = s.module || "Outros";
      if (!(mod in index)) {
        index[mod] = groups.length;
        groups.push({ module: mod, items: [] });
      }
      groups[index[mod]].items.push(s);
    });
    return groups;
  }, [screens]);

  if (!isAdmin) {
    return (
      <Layout>
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-gray-900">Permissões</h1>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            Você não tem permissão para acessar esta tela.
          </div>
        </div>
      </Layout>
    );
  }

  const adminTab = isAdminProfile(activeProfile);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Permissões</h1>
          <p className="text-gray-500">
            Defina quais telas cada perfil pode ver e quais ações pode executar.
          </p>
        </div>

        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <strong>Administrador</strong> e <strong>owner</strong> têm acesso total
          (global) e não podem ser alterados. Os demais perfis são por empresa
          {activeTenantName ? (
            <> — você está editando: <strong>{activeTenantName}</strong></>
          ) : null}
          . Crie perfis próprios em <strong>Novo perfil</strong> e marque as telas que cada um
          pode ver — o que ficar sem <strong>Ver</strong> nem aparece no menu. Usuários
          precisam recarregar a página após mudanças.
        </div>

        <div className="rounded-lg border bg-white">
          {/* Abas de perfil (dinâmicas) */}
          <div className="border-b px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              {profiles.map((p) => {
                const active = activeProfile === p.name;
                return (
                  <div
                    key={p.name}
                    className={`flex items-center rounded-md ${
                      active ? "bg-blue-600" : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => { setActiveProfile(p.name); setMessage(""); }}
                      className={`rounded-md py-2 pl-4 text-sm font-medium ${
                        p.is_system ? "pr-4" : "pr-2"
                      } ${active ? "text-white" : "text-gray-700"}`}
                    >
                      {p.name}
                      {p.user_count > 0 && (
                        <span className={`ml-1.5 text-xs ${active ? "text-blue-100" : "text-gray-400"}`}>
                          ({p.user_count})
                        </span>
                      )}
                    </button>
                    {!p.is_system && (
                      <button
                        type="button"
                        onClick={() => handleDeleteProfile(p)}
                        disabled={busyProfile}
                        className={`pl-1 pr-2 ${active ? "text-blue-100 hover:text-white" : "text-gray-400 hover:text-red-600"}`}
                        aria-label={`Excluir perfil ${p.name}`}
                        title="Excluir perfil"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                );
              })}

              {addingProfile ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateProfile();
                      if (e.key === "Escape") { setAddingProfile(false); setNewProfileName(""); }
                    }}
                    placeholder="Nome do perfil (ex.: Financeiro)"
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button type="button" onClick={handleCreateProfile} disabled={busyProfile || !newProfileName.trim()} className="h-auto px-3 py-2">
                    {busyProfile ? "..." : "Criar"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => { setAddingProfile(false); setNewProfileName(""); }} className="h-auto px-2 py-2 text-gray-500">
                    Cancelar
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" onClick={() => setAddingProfile(true)} className="h-auto gap-1.5 px-3 py-2 text-gray-700">
                  <Plus size={16} /> Novo perfil
                </Button>
              )}
            </div>
          </div>

          <div className="p-6 space-y-5">
            {loading ? (
              <div className="text-gray-500">Carregando permissões...</div>
            ) : (
              <>
                {message && (
                  <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-700">
                    {message}
                  </div>
                )}
                {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                    {error}
                  </div>
                )}

                {adminTab && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    O Administrador tem <strong>acesso total</strong> a todas as telas
                    e ações. Esta configuração não é editável.
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-600">
                        <th className="py-2 pr-4 font-semibold">Tela</th>
                        {ACTIONS.map((a) => (
                          <th key={a.key} className="px-3 py-2 text-center font-semibold w-20">
                            {a.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.map((group) => (
                        <React.Fragment key={group.module}>
                          <tr className="bg-gray-50">
                            <td
                              colSpan={1 + ACTIONS.length}
                              className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >
                              {group.module}
                            </td>
                          </tr>
                          {group.items.map((s) => {
                            const c = cell(activeProfile, s.id);
                            return (
                              <tr key={s.id} className="border-b last:border-0">
                                <td className="py-2.5 pr-4">
                                  <div className="font-medium text-gray-900">{s.name}</div>
                                  <div className="text-xs text-gray-400 font-mono">{s.route}</div>
                                </td>
                                {ACTIONS.map((a) => {
                                  const disabledByView =
                                    a.key !== "can_view" && !c.can_view;
                                  const disabled = adminTab || disabledByView;
                                  return (
                                    <td key={a.key} className="px-3 py-2.5 text-center">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 accent-blue-600 disabled:opacity-40"
                                        checked={!!c[a.key]}
                                        disabled={disabled}
                                        onChange={() => toggle(activeProfile, s.id, a.key)}
                                        aria-label={`${a.label} ${s.name}`}
                                      />
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!adminTab && (
                  <div className="flex justify-end">
                    <Button type="button" onClick={handleSave} disabled={saving}>
                      {saving ? "Salvando..." : `Salvar permissões — ${activeProfile}`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Permissoes;
